import React, { useContext, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthContext } from './context/AuthContext';
import { ThemeContext } from './context/ThemeContext';
import ProtectedRoute from './ProtectedRoute';

import HomePage from './pages/HomePage';
import DashboardPage from './pages/DashboardPage';
import AdminPage from './pages/AdminPage';
import MaterialDetailPage from './pages/MaterialDetailPage';
import ErrorPage from './pages/ErrorPage';

import Register from './components/Auth/Register';
import Login from './components/Auth/Login';
import VerifyEmail from './components/Auth/VerifyEmail';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import HtmlViewer from './components/Common/HtmlViewer';

import './App.scss';
import AppLoader from './components/Common/AppLoader';
import DevToolsBlocker from './components/Common/DevToolsBlocker';
import SupportChat from './components/Dashboard/SupportChat';
// FIX: Removed duplicate import of global.scss (already imported in index.js)

// --- Protection against unauthorized PDF downloads ---
const originalToDataURL = HTMLCanvasElement.prototype.toDataURL;
HTMLCanvasElement.prototype.toDataURL = function(...args) {
  if (this.classList && this.classList.contains('react-pdf__Page__canvas')) {
    console.warn('Action blocked: Canvas export is disabled for study materials.');
    return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII='; // 1x1 transparent pixel
  }
  return originalToDataURL.apply(this, args);
};

const originalToBlob = HTMLCanvasElement.prototype.toBlob;
if (originalToBlob) {
  HTMLCanvasElement.prototype.toBlob = function(...args) {
    if (this.classList && this.classList.contains('react-pdf__Page__canvas')) {
      console.warn('Action blocked: Canvas export is disabled for study materials.');
      if (typeof args[0] === 'function') {
        args[0](new Blob([]));
      }
      return;
    }
    return originalToBlob.apply(this, args);
  };
}
// -----------------------------------------------------
function App() {
  // FIX: Removed duplicate checkAuthStatus() call — AuthContext already runs it on mount
  // FIX: Removed duplicate document.body.className setter — ThemeContext already handles it
  // FIX: Removed unused `user` destructure — only `loading` is needed here
  const { loading } = useContext(AuthContext);

  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
    };

    const handleKeyDown = (e) => {
      if (
        (e.key === 'F12') ||
        (e.ctrlKey && e.shiftKey && e.key === 'I') ||
        (e.ctrlKey && e.shiftKey && e.key === 'J') ||
        (e.ctrlKey && e.key === 'u')
      ) {
        e.preventDefault();
      }
    };

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  if (loading) {
    return <AppLoader />;
  }

  return (
    <Router>
      <AppContent />
    </Router>
  );
}

const AppContent = () => {
  const { user } = useContext(AuthContext);
  // FIX: Moved useContext call to top level — hooks cannot be called inside JSX expressions
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();

  const handleHtmlViewerBack = () => {
    navigate('/dashboard', { state: { previousView: 'announcements' } });
  };

  if (user && user.isLocked) {
    return (
      <div className={`app-container ${theme}`} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--bg-color)', padding: '2rem' }}>
        <div style={{ maxWidth: '600px', width: '100%', background: 'var(--card-bg)', padding: '2rem', borderRadius: '12px', boxShadow: 'var(--card-shadow)' }}>
          <h2 style={{ color: 'var(--error-text)', textAlign: 'center', marginBottom: '1rem' }}>Account Suspended</h2>
          <p style={{ textAlign: 'center', color: 'var(--text-color)', marginBottom: '2rem' }}>
            Your account has been locked due to repeated attempts to bypass security controls by accessing Developer Tools. Please contact the administrator below to restore access.
          </p>
          <SupportChat />
        </div>
      </div>
    );
  }

  return (
    // FIX: Now uses the `theme` variable declared at the top of the component
    <div className={`app-container ${theme}`}>
      <DevToolsBlocker />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/register" element={user ? <Navigate to="/dashboard" /> : <Register />} />
        <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <Login />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/materials/:id" element={<ProtectedRoute><MaterialDetailPage /></ProtectedRoute>} />

        <Route
          path="/syllabus-view"
          element={
            <ProtectedRoute>
              <HtmlViewer
                title="ECE 2nd Year Syllabus"
                src={`${process.env.PUBLIC_URL}/syllabus.html`}
                onBack={handleHtmlViewerBack}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path="/guidelines-view"
          element={
            <ProtectedRoute>
              <HtmlViewer
                title="Subject Guidelines"
                src={`${process.env.PUBLIC_URL}/subject-guidelines.html`}
                onBack={handleHtmlViewerBack}
              />
            </ProtectedRoute>
          }
        />

      <Route path="/admin" element={<ProtectedRoute staffOnly={true}><AdminPage /></ProtectedRoute>} />
        <Route path="*" element={<ErrorPage />} />
      </Routes>
    </div>
  );
};

export default App;
